/**
 * 被曝マップ画面（§7.1）＋日陰考慮ルート検索。
 *
 * 出発地は「現在地」または「指定の場所」から選べる。目的地とあわせて、地名検索・
 * 地図タップのどちらでも指定できる。指定すると3パターンの経路を表示する：
 *   最短／日陰重視（時間≤最短×比率）／日陰重視（時間≤最短＋加算分）
 * 上限は設定画面で変更可能。経路の濃紺区間＝日陰、本来色の区間＝日向。
 * 出発時刻・到着時刻を指定でき、その時刻の太陽位置・気象予報（3日先まで）で計算する。
 */
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  areaSizeLabel,
  adviseDeparture,
  departureAdviceMessage,
  describeSunburn,
  downsampleWaypoints,
  googleMapsDirectionsUrl,
  NON_WALKING_NOTE,
  normalizeGeoQuery,
  planRoutes,
  POOL_UNIT_NOTE,
  solarPosition,
  supportsShadeRouting,
  TRAVEL_MODE_LABEL,
} from "@uvk/shared";
import type {
  DepartureAdvice,
  GeoResult,
  LatLon,
  RoutePlan,
  RouteResult,
  TravelMode,
} from "@uvk/shared";
import { activeArea, maybeSwitchArea, routePrefs, state } from "../state.js";
import { terrainCellAt, terrainTile } from "../terrainService.js";
import { ensureWx, wxAt } from "../wxService.js";
import { geocode } from "../api.js";
import { esc } from "../ui.js";

/** 現在地が取れないときの仮の出発地（設定中のエリアの代表地点） */
function defaultPos(): LatLon {
  return activeArea().defaultPos;
}
/**
 * 日陰区間の色。ルート識別色（暖色・寒色）とぶつからないグレーにする。
 * 濃紺だと「日陰①」の水色と紛らわしく、どのルートの日陰か読み取れなかった。
 */
const SHADE_COLOR = "#374151";

/**
 * ルートの識別色。
 * 最短は赤にしている。アプリのテーマ色（琥珀）や出発地マーカーと同系だと
 * 地図上で見分けにくかったのと、「いちばん日を浴びる道」という意味にも合う。
 */
const ROUTE_STYLE = [
  { key: "shortest", label: "最短", color: "#dc2626" },
  { key: "shadeWithinRatio", label: "日陰①", color: "#0ea5e9" },
  { key: "shadeWithinExtra", label: "日陰②", color: "#10b981" },
] as const;

type Field = "origin" | "dest";
type OriginMode = "current" | "fixed";
type TimeMode = "now" | "depart" | "arrive";

let map: maplibregl.Map | null = null;
/**
 * 地図に addLayer できる状態か（load イベントで true）。
 * isStyleLoaded() は load 後も false を返すことがあり、それを条件にすると
 * 経路線が一度も描かれないまま終わる。判定は load 到達だけに任せる。
 */
let mapReady = false;
/** 描画を頼まれたが地図がまだ用意できていない状態 */
let drawPending = false;
let startMarker: maplibregl.Marker | null = null;
let goalMarker: maplibregl.Marker | null = null;

/** マップ画面で取得した現在地（外出モードの計測とは独立に保持する） */
let currentPos: LatLon | null = null;
let originMode: OriginMode = "current";
let originFixed: LatLon | null = null;
let originName = "";
let goal: LatLon | null = null;
let goalName = "";
/** 次の地図タップが設定する対象 */
let tapTarget: Field = "dest";

let plan: RoutePlan | null = null;
let planDepartMs = 0;
/** 出発時刻をずらす提案（予報ベース） */
let advice: DepartureAdvice | null = null;
/**
 * 提案を採用した直後は再提案しない。朝はUVが上がり続けるため、
 * 抑制しないと「さらに30分早く」を押すたびに繰り返してしまう（§9.2の抑制と同じ考え方）。
 * 目的地・出発地・移動手段・時刻を自分で変えたら解除する。
 */
let adviceApplied = false;
let computing = false;
/** 計算中に条件が変わったことを覚えておき、終わり次第もう一度計算する */
let pendingRecompute = false;
let travelMode: TravelMode = "walking";
let timeMode: TimeMode = "now";
let timeValue = ""; // datetime-local

interface SearchState {
  searching: boolean;
  candidates: GeoResult[];
  note: string;
}
const searches: Record<Field, SearchState> = {
  origin: { searching: false, candidates: [], note: "" },
  dest: { searching: false, candidates: [], note: "" },
};

/** 実測またはマップ画面で取得した現在地。どちらも無ければ null */
function currentLocation(): LatLon | null {
  return state.engine.lastFix ?? currentPos;
}

function startPos(): LatLon {
  if (originMode === "fixed" && originFixed) return originFixed;
  return currentLocation() ?? defaultPos();
}

/** 出発地の表示名 */
function startLabel(): string {
  if (originMode === "fixed") return originName || "未指定";
  return currentLocation() ? "現在地" : `${activeArea().defaultPosName}（現在地が未取得のため仮）`;
}

/** 地形タイルのカバー範囲（検索バイアス用 viewbox: west,north,east,south） */
function coverageViewbox(): string | undefined {
  const tile = terrainTile(state.settings.heightMode, activeArea().id);
  if (!tile) return undefined;
  const b = tile.bounds();
  return `${b.west},${b.north},${b.east},${b.south}`;
}

/** 座標がカバー範囲内か（範囲外なら経路計算できない旨を案内する） */
function withinCoverage(pos: LatLon): boolean {
  const tile = terrainTile(state.settings.heightMode, activeArea().id);
  if (!tile) return false;
  const b = tile.bounds();
  return pos.lat >= b.south && pos.lat <= b.north && pos.lon >= b.west && pos.lon <= b.east;
}

function setOrigin(pos: LatLon, name: string, reveal = false): void {
  originMode = "fixed";
  originFixed = pos;
  originName = name;
  adviceApplied = false;
  updateOriginRow();
  syncMarkers();
  frameRoute(pos);
  void computeRoutes().then(() => {
    if (reveal && goal) {
      document.getElementById("route-panel")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  });
}

function setGoal(pos: LatLon, name: string, reveal = false): void {
  goal = pos;
  goalName = name;
  adviceApplied = false;
  updateDestLabel();
  syncMarkers();
  frameRoute(pos);
  // 候補から選んだ直後は、結果まで自動でスクロールして「指定できた」ことを見せる
  void computeRoutes().then(() => {
    if (reveal) document.getElementById("route-panel")?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
}

/** 確定した目的地を欄の下に表示する（出発地と同じ手応えを与える） */
function updateDestLabel(): void {
  const el = document.getElementById("dest-label");
  if (!el) return;
  el.innerHTML = goal
    ? `📍 目的地：<b>${esc(goalName)}</b>`
    : "";
}

/** 出発地と目的地の両方が入るよう表示範囲を合わせる（片方だけならそこへ寄せる） */
function frameRoute(fallback: LatLon): void {
  if (!map) return;
  const s = startPos();
  if (goal) {
    map.fitBounds(
      [
        [Math.min(s.lon, goal.lon), Math.min(s.lat, goal.lat)],
        [Math.max(s.lon, goal.lon), Math.max(s.lat, goal.lat)],
      ],
      { padding: 60, duration: 500, maxZoom: 17 },
    );
  } else {
    map.easeTo({ center: [fallback.lon, fallback.lat], duration: 500 });
  }
}

function syncMarkers(): void {
  if (!map) return;
  const s = startPos();
  if (startMarker) {
    startMarker.setLngLat([s.lon, s.lat]);
  } else {
    startMarker = new maplibregl.Marker({ color: "#f59e0b" }).setLngLat([s.lon, s.lat]).addTo(map);
  }
  if (goal) {
    if (goalMarker) {
      goalMarker.setLngLat([goal.lon, goal.lat]);
    } else {
      goalMarker = new maplibregl.Marker({ color: "#dc2626" })
        .setLngLat([goal.lon, goal.lat])
        .addTo(map);
    }
  }
}

// ---- 地名検索（出発地・目的地で共通） -------------------------------------

async function runSearch(field: Field, query: string): Promise<void> {
  const q = normalizeGeoQuery(query);
  const st = searches[field];
  if (!q || st.searching) return;
  st.searching = true;
  st.candidates = [];
  st.note = "";
  updateSearchPanel(field);

  const results = await geocode(q, coverageViewbox());
  st.searching = false;
  st.candidates = results;
  if (results.length === 0) {
    st.note = "見つかりませんでした。別の言い方や住所で試してください";
  } else if (!results.some((r) => withinCoverage(r))) {
    st.note = `候補はすべて地形データの範囲外です（対象は「${activeArea().name}」のみ）`;
  }
  updateSearchPanel(field);
}

function updateSearchPanel(field: Field): void {
  const el = document.getElementById(`${field}-results`);
  if (!el) return;
  const st = searches[field];
  if (st.searching) {
    el.innerHTML = `<p class="sub">検索中…</p>`;
    return;
  }
  const rows = st.candidates
    .map((c, i) => {
      const outside = withinCoverage(c) ? "" : `<span class="pill">範囲外</span>`;
      return `<button class="ghost" data-cand="${i}" style="width:100%;text-align:left;margin:4px 0">
          <b>${esc(c.name)}</b> ${outside}<br/><span class="sub">${esc(c.fullName)}</span>
        </button>`;
    })
    .join("");
  el.innerHTML = rows + (st.note ? `<p class="sub">${esc(st.note)}</p>` : "");

  el.querySelectorAll<HTMLButtonElement>("[data-cand]").forEach((b) => {
    b.onclick = () => {
      const c = st.candidates[Number(b.dataset.cand)];
      if (!c) return;
      st.candidates = [];
      st.note = "";
      updateSearchPanel(field);
      const pos = { lat: c.lat, lon: c.lon };
      if (field === "origin") setOrigin(pos, c.name, true);
      else setGoal(pos, c.name, true);
    };
  });
}

/** 検索欄＋「地図で指定」ボタンの共通マークアップ */
function searchRowHtml(field: Field, placeholder: string): string {
  return `
    <div class="row">
      <input type="text" id="${field}-input" class="grow" placeholder="${placeholder}" />
      <button id="${field}-search" class="ghost">🔍</button>
      <button id="${field}-pick" class="ghost">地図で指定</button>
    </div>
    <div id="${field}-results"></div>`;
}

function bindSearchRow(field: Field): void {
  const input = document.getElementById(`${field}-input`) as HTMLInputElement | null;
  const btn = document.getElementById(`${field}-search`) as HTMLButtonElement | null;
  const pick = document.getElementById(`${field}-pick`) as HTMLButtonElement | null;
  if (!input || !btn || !pick) return;
  btn.onclick = () => void runSearch(field, input.value);
  input.onkeydown = (ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      void runSearch(field, input.value);
    }
  };
  pick.onclick = () => {
    tapTarget = field;
    const hint = document.getElementById("tap-hint");
    if (hint) {
      hint.textContent = `地図をタップして${field === "origin" ? "出発地" : "目的地"}を指定してください`;
      hint.hidden = false;
    }
  };
}

// ---- 出発地の行（モードで中身が変わるため差し替え式） ----------------------

function updateOriginRow(): void {
  const el = document.getElementById("origin-row");
  if (!el) return;
  el.innerHTML = `
    <div class="row">
      <select id="origin-mode" style="width:130px">
        <option value="current" ${originMode === "current" ? "selected" : ""}>現在地から</option>
        <option value="fixed" ${originMode === "fixed" ? "selected" : ""}>指定の場所から</option>
      </select>
      ${
        originMode === "current"
          ? `<button id="locate-btn" class="ghost grow">📍 現在地を取得</button>`
          : `<span class="sub grow">${esc(originName || "場所を指定してください")}</span>`
      }
    </div>
    ${originMode === "fixed" ? searchRowHtml("origin", `例：${activeArea().defaultPosName}`) : ""}
    <p class="sub">出発地：${esc(startLabel())}</p>
  `;

  (el.querySelector("#origin-mode") as HTMLSelectElement).onchange = (ev) => {
    originMode = (ev.target as HTMLSelectElement).value as OriginMode;
    searches.origin = { searching: false, candidates: [], note: "" };
    updateOriginRow();
    syncMarkers();
    void computeRoutes();
  };

  const locate = el.querySelector("#locate-btn") as HTMLButtonElement | null;
  if (locate) {
    locate.onclick = () => {
      locate.disabled = true;
      locate.textContent = "取得中…";
      navigator.geolocation.getCurrentPosition(
        async (p) => {
          currentPos = { lat: p.coords.latitude, lon: p.coords.longitude };
          // 現在地が別のエリア内なら自動で切り替える。タイルが変わるので読み直しが要る
          if (await maybeSwitchArea(currentPos)) {
            alert(`現在地に合わせて「${activeArea().name}」に切り替えます`);
            location.reload();
            return;
          }
          updateOriginRow();
          syncMarkers();
          frameRoute(currentPos);
          void computeRoutes();
        },
        () => {
          locate.disabled = false;
          locate.textContent = "📍 現在地を取得";
          alert("現在地を取得できませんでした");
        },
        { enableHighAccuracy: true, timeout: 15000 },
      );
    };
  }
  if (originMode === "fixed") bindSearchRow("origin");
}

// ---- ルート計算・描画 ------------------------------------------------------

/** 指定時刻の解決：出発ms（到着指定は所要時間を1回反復して逆算） */
function resolveDepartMs(shortestMin: number | null): number {
  const now = Date.now();
  if (timeMode === "now" || !timeValue) return now;
  const t = new Date(timeValue).getTime();
  if (Number.isNaN(t)) return now;
  if (timeMode === "depart") return t;
  return t - (shortestMin ?? 15) * 60000; // 到着指定：所要時間ぶん手前に出発
}

async function computeRoutes(): Promise<void> {
  const tile = terrainTile(state.settings.heightMode, activeArea().id);
  // 電車・自動車はアプリ内で経路探索しない（Googleマップへ委譲）
  if (!supportsShadeRouting(travelMode)) {
    plan = null;
    clearRoutes();
    updatePanel();
    return;
  }
  if (!tile || !goal) {
    updatePanel(); // 実行できない理由をボタン・パネルに反映する
    return;
  }
  // 計算は1本ずつ。実行中に条件が変わったら捨てずに、終わってから追いかける
  if (computing) {
    pendingRecompute = true;
    return;
  }

  computing = true;
  updatePanel();
  await new Promise((r) => setTimeout(r, 30)); // 「計算中…」を描画させる

  try {
    do {
      pendingRecompute = false;
      const currentGoal = goal;
      if (!currentGoal) break;
      const start = startPos();
      const center: LatLon = {
        lat: (start.lat + currentGoal.lat) / 2,
        lon: (start.lon + currentGoal.lon) / 2,
      };
      // 到着指定は2パス：仮計算で所要時間を出し、出発時刻を逆算して再計算
      let departMs = resolveDepartMs(plan?.shortest.timeMin ?? null);
      for (let pass = 0; pass < (timeMode === "arrive" ? 2 : 1); pass++) {
        await ensureWx(center, departMs);
        const sun = solarPosition(departMs, center);
        const wx = wxAt(center, departMs);
        plan = planRoutes({
          tile,
          start,
          goal: currentGoal,
          sun: { elevDeg: sun.elevDeg, azDeg: sun.azDeg },
          wx,
          prefs: routePrefs(),
        });
        if (timeMode === "arrive" && plan) departMs = resolveDepartMs(plan.shortest.timeMin);
      }
      planDepartMs = departMs;

      // 同じ経路のまま出発時刻だけずらして、予報上より少ない時刻を探す
      advice = plan
        ? adviseDeparture({
            tile,
            path: plan.shadeWithinExtra.path,
            baseDepartMs: departMs,
            wxAt: (ms) => {
              const w = wxAt(center, ms);
              return { uvi: w.uvi, uviClearSky: w.uviClearSky, fdBb: w.fdBb };
            },
          })
        : null;
    } while (pendingRecompute);
  } finally {
    computing = false;
  }
  drawRoutes();
  updatePanel();
}

/** 経路を2点セグメントのFeatureCollectionへ（shadeプロパティで日陰区間を色分け） */
function routeGeoJson(r: RouteResult): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (let i = 1; i < r.path.length; i++) {
    const a = r.path[i - 1]!;
    const b = r.path[i]!;
    features.push({
      type: "Feature",
      properties: { shade: r.sunnyFlags[i] ? 0 : 1 },
      geometry: {
        type: "LineString",
        coordinates: [
          [a.lon, a.lat],
          [b.lon, b.lat],
        ],
      },
    });
  }
  return { type: "FeatureCollection", features };
}

/**
 * 日陰ルートを計算できる範囲（地形タイルのカバー範囲）を地図に示す。
 * 地図はどこまでもスクロールできる一方、計算できるのはこの矩形の中だけなので、
 * 見えていないと「検索してもルートが出ない」の原因が分からない。
 */
function drawCoverage(): void {
  const tile = terrainTile(state.settings.heightMode, activeArea().id);
  if (!map || !tile || map.getSource("coverage")) return;
  const b = tile.bounds();
  map.addSource("coverage", {
    type: "geojson",
    data: {
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [b.west, b.south],
            [b.east, b.south],
            [b.east, b.north],
            [b.west, b.north],
            [b.west, b.south],
          ],
        ],
      },
    },
  });
  map.addLayer({
    id: "coverage-fill",
    type: "fill",
    source: "coverage",
    paint: { "fill-color": "#f59e0b", "fill-opacity": 0.06 },
  });
  map.addLayer({
    id: "coverage-line",
    type: "line",
    source: "coverage",
    paint: { "line-color": "#f59e0b", "line-width": 2, "line-dasharray": [3, 2] },
  });
}

/** 描画済みの経路線を消す（移動手段の切替・条件変更時） */
function clearRoutes(): void {
  if (!map || !mapReady) return;
  ROUTE_STYLE.forEach((_, i) => {
    const id = `route-${i}`;
    if (map!.getLayer(id)) map!.removeLayer(id);
    if (map!.getSource(id)) map!.removeSource(id);
  });
}

function drawRoutes(): void {
  if (!map || !plan) return;
  // 読み込み前だと addLayer できない。予約しておき、load 後に描き直す。
  if (!mapReady) {
    drawPending = true;
    return;
  }
  ROUTE_STYLE.forEach((s, i) => {
    const r = plan![s.key];
    const id = `route-${i}`;
    const data = routeGeoJson(r);
    const src = map!.getSource(id) as maplibregl.GeoJSONSource | undefined;
    if (src) {
      src.setData(data);
    } else {
      map!.addSource(id, { type: "geojson", data });
      map!.addLayer({
        id,
        type: "line",
        source: id,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          // 日陰区間は濃紺、日向区間はルート色
          "line-color": ["case", ["==", ["get", "shade"], 1], SHADE_COLOR, s.color],
          // 日陰ルートを少し太くして主役にする。最短も細すぎると見えないので4pxは確保
          "line-width": i === 0 ? 4 : 5.5,
          "line-opacity": 0.9,
        },
      });
    }
  });
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
}

/**
 * 検索ボタンの状態。条件を変えると自動で再計算されるが、
 * 「押して実行する」操作を探す人のために明示ボタンも用意している。
 */
function updateRunButton(): void {
  const btn = document.getElementById("route-run") as HTMLButtonElement | null;
  if (!btn) return;
  if (computing) {
    btn.disabled = true;
    btn.textContent = "ルートを計算中…";
  } else if (!goal) {
    btn.disabled = true;
    btn.textContent = "目的地を指定してください";
  } else if (!supportsShadeRouting(travelMode)) {
    btn.disabled = false;
    btn.textContent = `🗺️ Googleマップで${TRAVEL_MODE_LABEL[travelMode]}ルートを見る`;
  } else {
    btn.disabled = false;
    btn.textContent = plan ? "🔍 この条件で再検索" : "🔍 ルートを検索";
  }
}

/** Googleマップを開く（ナビは向こうに任せる） */
function openGoogleMaps(route?: RouteResult): void {
  if (!goal) return;
  window.open(
    googleMapsDirectionsUrl({
      origin: startPos(),
      destination: goal,
      mode: travelMode,
      waypoints: route ? downsampleWaypoints(route.path) : undefined,
    }),
    "_blank",
    "noopener",
  );
}

function updatePanel(): void {
  updateRunButton();
  const el = document.getElementById("route-panel");
  if (!el) return;
  if (computing) {
    el.innerHTML = `<p class="sub">ルートを計算中…</p>`;
    return;
  }
  if (!goal) {
    el.innerHTML = `<p class="sub">目的地を入力するか、地図をタップして指定してください</p>`;
    return;
  }
  // 電車・自動車：アプリ内では経路探索せず、できないことを説明してGoogleマップへ渡す
  if (!supportsShadeRouting(travelMode)) {
    el.innerHTML = `
      <p class="sub">${esc(NON_WALKING_NOTE[travelMode as "transit" | "driving"])}</p>
      <button id="gmap-open" class="ghost" style="width:100%">
        🗺️ Googleマップで${TRAVEL_MODE_LABEL[travelMode]}ルートを見る
      </button>`;
    (el.querySelector("#gmap-open") as HTMLButtonElement).onclick = () => openGoogleMaps();
    return;
  }
  if (!plan) {
    const outside = !withinCoverage(goal) || !withinCoverage(startPos());
    el.innerHTML = `<p class="sub">${
      outside
        ? `出発地または目的地が地形データの範囲外です（対象は「${activeArea().name}」の約${areaSizeLabel(activeArea())}、地図の破線の内側）`
        : "この区間の歩行ルートが見つかりませんでした"
    }</p>`;
    return;
  }
  const prefs = plan.prefs;
  const capLabel = ["", `≤最短×${prefs.maxTimeRatio}`, `≤最短+${prefs.maxExtraMinutes}分`];

  // 出発時刻をずらす提案（あれば先頭に出す）
  const adviceMsg = advice && !adviceApplied ? departureAdviceMessage(advice) : null;
  // 表示の基準は、下のカードに出ている「日陰②」の値に合わせる。
  // 提案の内部計算は歩行中の太陽の動きも織り込むため値がわずかに異なり、
  // そのまま出すと同じ画面に別の数字が並んでしまう（削減率だけを借りる）。
  const adviceBaseSed = plan.shadeWithinExtra.sed;
  const adviceBestSed = adviceBaseSed * (1 - (advice?.reductionRatio ?? 0));
  const adviceHtml = adviceMsg
    ? `<div class="alert-item" style="margin-bottom:10px">
         <b>⏰ ${esc(adviceMsg)}</b>
         <div class="row" style="margin-top:6px">
           <span class="sub grow">日陰②のルートなら、${fmtTime(advice!.best.departMs)}発で ${describeSunburn(adviceBestSed).poolTime}（いまの予定は ${describeSunburn(adviceBaseSed).poolTime}）</span>
           <button id="apply-advice" class="ghost">この時刻にする</button>
         </div>
       </div>`
    : "";
  // 最も日焼けの少ないルートに印をつける（3本が同じ経路になることもある）
  const minSed = Math.min(...ROUTE_STYLE.map((s) => plan![s.key].sed));

  el.innerHTML =
    adviceHtml +
    ROUTE_STYLE.map((s, i) => {
      const r = plan![s.key];
      const dep = fmtTime(planDepartMs);
      const arr = fmtTime(planDepartMs + r.timeMin * 60000);
      const burn = describeSunburn(r.sed);
      const best = r.sed <= minSed + 1e-9 ? `<span class="pill sun">日焼け最少</span>` : "";
      return `
      <div class="stat" style="margin-bottom:8px">
        <div class="row">
          <span class="pill" style="background:${s.color};color:#fff">${s.label}</span>
          <span class="sub grow">${capLabel[i] ?? ""}</span>
          <b>${r.timeMin.toFixed(0)}分</b>
        </div>
        <div class="row" style="margin-top:4px">
          <span style="font-size:18px;font-weight:700">☀️ ${esc(burn.poolTime)}</span>
          <span class="sub">${esc(burn.p)}</span>
          ${best}
        </div>
        <div class="sub">
          日焼け量 ${esc(burn.sed)}（日焼け止めなしの場合）／ 日陰率 ${Math.round(r.shadeShare * 100)}%
        </div>
        <div class="row" style="margin-top:6px">
          <span class="sub grow">${dep}発 → ${arr}着</span>
          <button class="ghost" data-gmap="${s.key}">🗺️ ナビ</button>
        </div>
      </div>`;
    }).join("") +
    `<p class="sub">${esc(startLabel())} → <b>${esc(goalName || "指定地点")}</b>／` +
    `経路の<b style="color:${SHADE_COLOR}">グレーの区間が日陰</b>、色のついた区間が日向です</p>` +
    `<p class="sub">${esc(POOL_UNIT_NOTE)}</p>` +
    `<p class="sub">「🗺️ ナビ」はそのルートを経由地つきでGoogleマップに渡します（案内はGoogleマップ側）</p>`;

  el.querySelectorAll<HTMLButtonElement>("[data-gmap]").forEach((b) => {
    b.onclick = () => openGoogleMaps(plan![b.dataset.gmap as keyof RoutePlan as "shortest"]);
  });

  // 提案された時刻を出発時刻として採用し、その条件で計算し直す
  const applyBtn = el.querySelector<HTMLButtonElement>("#apply-advice");
  if (applyBtn) {
    applyBtn.onclick = () => {
      const best = advice!.best.departMs;
      adviceApplied = true;
      timeMode = "depart";
      const local = new Date(best - new Date().getTimezoneOffset() * 60000);
      timeValue = local.toISOString().slice(0, 16);
      const modeSel = document.getElementById("time-mode") as HTMLSelectElement | null;
      const timeInput = document.getElementById("time-value") as HTMLInputElement | null;
      if (modeSel) modeSel.value = "depart";
      if (timeInput) {
        timeInput.disabled = false;
        timeInput.value = timeValue;
      }
      void computeRoutes();
    };
  }
}

export function renderMap(el: HTMLElement): void {
  // 地図・操作パネルは初回のみ生成し、以降は情報だけ更新する
  if (!el.querySelector("#map-container")) {
    el.innerHTML = `
      <div class="card">
        <h2>🗺️ 日陰ルート検索</h2>
        <div class="row" id="mode-row" style="margin-bottom:10px">
          ${(Object.keys(TRAVEL_MODE_LABEL) as TravelMode[])
            .map(
              (m) =>
                `<button class="ghost grow ${m === travelMode ? "selected" : ""}" data-mode="${m}">
                   ${{ walking: "🚶", transit: "🚃", driving: "🚗" }[m]} ${TRAVEL_MODE_LABEL[m]}
                 </button>`,
            )
            .join("")}
        </div>
        <div id="origin-row"></div>
        <label class="field">目的地</label>
        ${searchRowHtml("dest", activeArea().searchExamples)}
        <p class="sub" id="dest-label"></p>
        <p class="sub" id="tap-hint" hidden></p>
        <div class="row" style="margin:8px 0">
          <select id="time-mode" style="width:130px">
            <option value="now">今すぐ出発</option>
            <option value="depart">出発時刻指定</option>
            <option value="arrive">到着時刻指定</option>
          </select>
          <input type="datetime-local" id="time-value" class="grow" disabled />
        </div>
        <button id="route-run" class="primary" style="width:100%;margin-bottom:10px" disabled></button>
        <div id="map-container"></div>
        <div class="row" id="map-legend" style="margin-top:8px;font-size:12px;gap:12px">
          ${ROUTE_STYLE.map(
            (s) =>
              `<span><span style="display:inline-block;width:14px;height:4px;border-radius:2px;background:${s.color};vertical-align:middle"></span> ${s.label}</span>`,
          ).join("")}
          <span><span style="display:inline-block;width:14px;height:4px;border-radius:2px;background:${SHADE_COLOR};vertical-align:middle"></span> <b>日陰の区間</b></span>
        </div>
        <p class="sub">色は<b>どのルートか</b>、グレーは<b>そこが日陰</b>であることを表します</p>
        <p class="sub" id="map-info"></p>
      </div>
      <div class="card">
        <h2>🚶 ルート比較 <span class="sub">上限は設定画面で変更できます</span></h2>
        <div id="route-panel"></div>
      </div>
      <p class="credit">Weather data by <a href="https://open-meteo.com/" target="_blank" rel="noreferrer">Open-Meteo.com</a> (CC BY 4.0)／地図 © OpenStreetMap contributors／建物形状 国土交通省PLATEAU</p>
    `;

    const pos = startPos();
    map = new maplibregl.Map({
      container: el.querySelector<HTMLElement>("#map-container")!,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution: "© OpenStreetMap contributors",
          },
        },
        layers: [{ id: "osm", type: "raster", source: "osm" }],
      },
      center: [pos.lon, pos.lat],
      zoom: 15,
    });
    (window as unknown as Record<string, unknown>).__uvkMap = map; // E2Eテスト用フック
    mapReady = false;
    map.on("load", () => {
      mapReady = true;
      drawCoverage();
      if (drawPending) {
        drawPending = false;
        drawRoutes();
      }
    });
    syncMarkers();

    map.on("click", (e) => {
      const p = { lat: e.lngLat.lat, lon: e.lngLat.lng };
      const hint = document.getElementById("tap-hint");
      if (hint) hint.hidden = true;
      if (tapTarget === "origin") {
        tapTarget = "dest"; // 既定は目的地指定に戻す
        setOrigin(p, "地図で指定した地点");
      } else {
        setGoal(p, "地図で指定した地点");
      }
    });

    bindSearchRow("dest");
    updateOriginRow();

    el.querySelector<HTMLButtonElement>("#route-run")!.onclick = () => {
      if (supportsShadeRouting(travelMode)) void computeRoutes();
      else openGoogleMaps();
    };

    el.querySelectorAll<HTMLButtonElement>("#mode-row [data-mode]").forEach((b) => {
      b.onclick = () => {
        travelMode = b.dataset.mode as TravelMode;
        el.querySelectorAll("#mode-row [data-mode]").forEach((x) => x.classList.remove("selected"));
        b.classList.add("selected");
        clearRoutes();
        plan = null;
        void computeRoutes();
      };
    });

    const modeSel = el.querySelector<HTMLSelectElement>("#time-mode")!;
    const timeInput = el.querySelector<HTMLInputElement>("#time-value")!;
    modeSel.onchange = () => {
      timeMode = modeSel.value as TimeMode;
      timeInput.disabled = timeMode === "now";
      if (timeMode !== "now" && !timeInput.value) {
        // 既定値：30分後（ローカル時刻でdatetime-local形式に整形）
        const d = new Date(Date.now() + 30 * 60000 - new Date().getTimezoneOffset() * 60000);
        timeInput.value = d.toISOString().slice(0, 16);
        timeValue = timeInput.value;
      }
      void computeRoutes();
    };
    timeInput.onchange = () => {
      timeValue = timeInput.value;
      adviceApplied = false; // 自分で時刻を変えたら、その時刻を基準に提案し直す
      void computeRoutes();
    };

    updateSearchPanel("dest");
    updateDestLabel();
    updatePanel();
  }

  syncMarkers();

  const here = startPos();
  const now = Date.now();
  const wx = wxAt(here, now);
  const cell = terrainCellAt(here, state.settings.heightMode, activeArea().id);
  const d = state.lastDetail;
  const sunState = d ? (d.indoor ? "屋内" : d.visible ? "☀️ 日向" : "⛅ 日陰") : "計測前";
  const dirPct = d && d.eSkin > 0 ? Math.round(d.directShare * 100) : 0;
  el.querySelector("#map-info")!.textContent =
    `現在地：${sunState} ／ UVI ${wx.uvi.toFixed(1)}${wx.degraded ? "（晴天仮定）" : ""} ／ ` +
    `天空率 ${(cell?.svf ?? 1).toFixed(2)} ／ 直達${dirPct}%・散乱${100 - dirPct}% ／ ` +
    `${(d?.sedRatePerH ?? 0).toFixed(2)} SED/h`;
}

export function destroyMap(): void {
  map?.remove();
  map = null;
  mapReady = false;
  drawPending = false;
  startMarker = null;
  goalMarker = null;
  plan = null;
  goal = null;
  goalName = "";
  originMode = "current";
  originFixed = null;
  originName = "";
  travelMode = "walking";
  tapTarget = "dest";
  searches.origin = { searching: false, candidates: [], note: "" };
  searches.dest = { searching: false, candidates: [], note: "" };
}
